import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of, defer } from "rxjs";
import { switchMap, map, catchError, filter, flatMap, takeUntil, pluck, exhaustMap } from "rxjs/operators";
import { selectCustomerIpCountry } from "@nxplatform/geo-data/slices/ip-data.slice";
import { groupArrayToKeyData, groupByMapProp } from "@nxplatform/helpers/common";
import { refetchOnFocus } from "@nxplatform/helpers/common/rxjs-helpers/refetch-on-focus";
import { FeedVendorType, EventStatus, SportConfigTemplates } from "@nxplatform/platform-entities/sport/common-sport.entities";
import { selectGrpcIsActive } from "@nxplatform/platform-settings/locale-setup.selectors";
import { getBetRadarSportDataSuccess } from "@nxplatform/store-shared/sport/shared-sport-actions";
import { BETRADAR_SETUP_FEATURE_KEY } from "@nxplatform/store-shared/sport/shared-sport-feature-keys";
import { fromWebSocket } from "@service/api-client/websockets/centrifuge";
import { WSNamespaces } from "@service/api-client/websockets/ws-client";
import { startWorker } from "./workers/sport-data.worker.js";
import { SportWorkerTasks } from "./workers/workers.types.js";
const initialBetradarSetupState = {
  outcomes: null,
  markets: null,
  status: null,
  loaded: false,
  error: null,
  groups: [],
  sportSorted: null,
  sportStarted: false,
  alive: {
    [FeedVendorType.S2G]: true,
    [FeedVendorType.betRadar]: true,
    [FeedVendorType.BetBy]: true
  },
  liveBetsEnabled: true,
  variants: /* @__PURE__ */ Object.create(null),
  competitors: /* @__PURE__ */ Object.create(null),
  liveOddsEnabled: {
    [FeedVendorType.S2G]: true,
    [FeedVendorType.betRadar]: true,
    [FeedVendorType.BetBy]: true
  },
  activeEvents: [],
  config: null,
  configLoaded: false
};
const updateFeedState = (feedInitialState, feed) => {
  return Object.keys(feedInitialState).reduce((feedState, vendorKey) => {
    return {
      ...feedState,
      [vendorKey]: feed.includes(Number(vendorKey))
    };
  }, {});
};
const betradarSetupSlice = createSlice({
  name: BETRADAR_SETUP_FEATURE_KEY,
  initialState: initialBetradarSetupState,
  reducers: {
    getBetradarSetupMarketStart: (state, action) => {
    },
    getBetradarVariantsSuccess: (state, action) => {
      const data = groupArrayToKeyData(action.payload, "id");
      Object.assign(state.variants, data);
    },
    competitorsTranslationsSuccess: (state, action) => {
      const data = groupArrayToKeyData(action.payload, "id");
      Object.assign(state.competitors, data);
    },
    getBetradarSetupMarketSuccess: (state, action) => {
      state.markets = action.payload;
      state.groups = action.payload.groups;
    },
    getBetradarSetupMarketFailure: (state, action) => {
      state.error = action.payload;
    },
    getBetradarSetupStatusStart: (state, action) => {
    },
    getBetradarSetupStatusSuccess: (state, action) => {
      state.status = action.payload;
    },
    getBetradarSetupStatusFailure: (state, action) => {
      state.error = action.payload;
    },
    getBetradarSetupOutcomesStart: (state, action) => {
    },
    getBetradarSetupOutcomesSuccess: (state, action) => {
      state.outcomes = action.payload;
      state.loaded = true;
    },
    getBetradarSetupOutcomesFailure: (state, action) => {
      state.error = action.payload;
    },
    getBetradarAliveStart: (state, action) => {
    },
    updateAliveStatus: (state, action) => {
      state.alive = action.payload;
    },
    updateLiveOdds: (state, action) => {
      state.liveOddsEnabled = action.payload;
    },
    updateLiveBetsEnabled: (state, action) => {
      state.liveBetsEnabled = action.payload;
    },
    getBetradarAliveSuccess: (state, action) => {
      const { feedIsUp, liveBetsEnabled, liveOddsEnabled } = action.payload;
      state.alive = updateFeedState(state.alive, feedIsUp);
      if (liveBetsEnabled !== void 0) {
        state.liveBetsEnabled = !!liveBetsEnabled;
      }
      if (action.payload.liveOddsEnabled !== void 0) {
        state.liveOddsEnabled = updateFeedState(state.liveOddsEnabled, liveOddsEnabled);
      }
    },
    getBetRadarAliveClear: (state) => {
      state.alive = initialBetradarSetupState.alive;
    },
    getBetRadarAliveFail: (state) => {
      state.alive = Object.keys(state.alive).reduce((feedState, vendorKey) => {
        return {
          ...feedState,
          [vendorKey]: false
        };
      }, {});
    },
    getBetRadarSportGroupSortingStart: (state) => {
    },
    getBetRadarSportGroupSortingSuccess: (state, action) => {
      state.sportSorted = action.payload;
    },
    getBetRadarSportDataStop: (state) => {
      state.sportStarted = false;
    },
    setBetRadarActiveEvents: (state, action) => {
      state.activeEvents = action.payload;
    },
    getSocketStart(state) {
    },
    getSocketSuccess(state, payload) {
    },
    getSocketFailed(state, payload) {
    },
    getSocketPublicData() {
    },
    getVariantListStart(state) {
    },
    getVariantListFailed(state, action) {
    },
    getCompetitorListStart(state) {
    },
    getCompetitorListFailed(state, action) {
    },
    getSportConfigStart(state) {
      state.configLoaded = false;
    },
    getSportConfigError() {
    },
    getSportConfigSuccess(state, action) {
      state.configLoaded = true;
      state.config = action.payload;
    },
    unsubscribeSportSocketDataStart() {
    },
    unsubscribeSportSocketDataSuccess() {
    },
    unsubscribeSportSocketDataError() {
    },
    unsubscribeSportEventsDataStart() {
    },
    unsubscribeSportEventsDataSuccess() {
    },
    unsubscribeSportEventsDataError() {
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getBetRadarSportDataSuccess, (state, action) => {
    });
  }
});
const betradarSetupReducer = betradarSetupSlice.reducer;
const {
  getBetradarSetupMarketStart,
  getBetradarSetupMarketSuccess,
  getBetradarSetupMarketFailure,
  getBetradarSetupOutcomesFailure,
  getBetradarSetupOutcomesSuccess,
  getBetradarSetupOutcomesStart,
  getBetradarAliveStart,
  getBetradarAliveSuccess,
  getBetRadarAliveClear,
  getBetRadarAliveFail,
  getBetRadarSportDataStop,
  getBetradarSetupStatusStart,
  getBetradarSetupStatusSuccess,
  getBetradarSetupStatusFailure,
  getBetRadarSportGroupSortingStart,
  getBetRadarSportGroupSortingSuccess,
  getBetradarVariantsSuccess,
  getSocketStart,
  getSocketFailed,
  getSocketSuccess,
  setBetRadarActiveEvents,
  getSocketPublicData,
  getVariantListStart,
  getVariantListFailed,
  getCompetitorListStart,
  getCompetitorListFailed,
  getSportConfigStart,
  getSportConfigError,
  getSportConfigSuccess,
  competitorsTranslationsSuccess,
  unsubscribeSportSocketDataStart,
  unsubscribeSportSocketDataSuccess,
  unsubscribeSportSocketDataError,
  updateAliveStatus,
  updateLiveOdds,
  updateLiveBetsEnabled,
  unsubscribeSportEventsDataSuccess,
  unsubscribeSportEventsDataStart,
  unsubscribeSportEventsDataError
} = betradarSetupSlice.actions;
const getBetradarSetupState = (rootState) => rootState[BETRADAR_SETUP_FEATURE_KEY];
const selectBetRadarOutcomesEntities = createSelector(
  getBetradarSetupState,
  (s) => {
    var _a;
    return (_a = s.outcomes) == null ? void 0 : _a.items;
  }
);
const selectBetRadarMarketsEntities = createSelector(
  getBetradarSetupState,
  (s) => {
    var _a;
    return (_a = s.markets) == null ? void 0 : _a.items;
  }
);
const selectBetRadarSportStarted = createSelector(
  getBetradarSetupState,
  (s) => s.sportStarted
);
const selectOutcomeCompetitors = createSelector(
  getBetradarSetupState,
  (s) => {
    return s.competitors;
  }
);
const selectSportsWithAdditionalSection = createSelector(
  getBetradarSetupState,
  (s) => {
    var _a;
    return ((_a = s.config) == null ? void 0 : _a.showPeriodData) ? s.config.showPeriodData : [];
  }
);
const selectBetRadarVariants = createSelector(
  getBetradarSetupState,
  (s) => s.variants
);
const selectBetRadarGroups = createSelector(getBetradarSetupState, (s) => Object.values(s.groups));
const selectAliveFeedsState = createSelector(
  getBetradarSetupState,
  (s) => s.alive
);
const selectLiveOddsState = createSelector(
  getBetradarSetupState,
  (s) => s.liveOddsEnabled
);
const selectAliveWithOddsState = createSelector(
  [selectAliveFeedsState, selectLiveOddsState],
  (aliveState, liveOddsState) => {
    return {
      aliveState,
      liveOddsState
    };
  }
);
const selectAliveState = createSelector(
  selectAliveWithOddsState,
  (aliveState, eventVendorId) => eventVendorId,
  ({ aliveState, liveOddsState }, eventVendorId) => {
    return !!(aliveState || {})[eventVendorId] && !!(liveOddsState || {})[eventVendorId];
  }
);
const selectBetRadarSetupLoaded = createSelector(
  getBetradarSetupState,
  (s) => {
    var _a, _b;
    return !!((_a = s.markets) == null ? void 0 : _a.items.length) && !!((_b = s.outcomes) == null ? void 0 : _b.items.length);
  }
);
const selectLiveBetsEnabled = createSelector(
  getBetradarSetupState,
  (s) => s.liveBetsEnabled
);
const selectSportConfigLoaded = createSelector(
  getBetradarSetupState,
  (s) => s.configLoaded
);
const selectSportSorted = createSelector(
  getBetradarSetupState,
  (s) => s.sportSorted || []
);
const selectSportSortedById = createSelector(
  selectSportSorted,
  (s, event) => event || null,
  (s, event) => {
    if (s && event && s[event.sportId]) {
      const eventIsLive = event.status === EventStatus.line ? Boolean(0) : Boolean(1);
      return s[event.sportId].filter((group) => group.isLive === eventIsLive);
    }
    return null;
  }
);
const selectStatusById = createSelector(
  getBetradarSetupState,
  (s, statusId) => Number(statusId),
  (s, statusId) => {
    var _a, _b;
    return ((_b = (_a = s.status) == null ? void 0 : _a.items.find((status) => Number(status.id) === statusId)) == null ? void 0 : _b.name) || null;
  }
);
const selectSportConfig = createSelector(
  getBetradarSetupState,
  (s) => s.config
);
const selectSportConfigBySportId = createSelector(
  selectSportConfig,
  (config, sportId) => Number(sportId),
  (config, sportId, isLive) => !!isLive,
  (s, sportId, isLive) => {
    const template = isLive ? SportConfigTemplates.live : SportConfigTemplates.line;
    const data = (s == null ? void 0 : s[template][sportId]) || (s == null ? void 0 : s.base);
    if (data && Array.isArray(data.markets)) {
      return data.markets.reduce((acc, market) => {
        const { metaData } = market;
        const { isHandicap, isTotal, withBase } = metaData || {};
        return [
          ...acc,
          ...market.outcomes.map(({ key, ...outcome }) => ({
            ...outcome,
            isHandicap,
            isTotal,
            withBase
          }))
        ];
      }, []);
    }
    return [];
  }
);
const selectSportConfigTitlesMap = createSelector(
  (config) => config,
  (config) => {
    if (config) {
      const groups = groupByMapProp(config, "marketId");
      return Array.from(groups.entries()).reduce((acc, marketEntry) => {
        const [marketId, outcomes] = marketEntry;
        const base = outcomes.find((outcome) => outcome.id === 0);
        const title = base || outcomes[0];
        acc.set(marketId, title);
        return acc;
      }, /* @__PURE__ */ new Map());
    }
    return null;
  }
);
const selectSportConfigMarketsBySportIds = createSelector(
  selectSportConfig,
  (config, sportIds) => sportIds,
  (config, sportIds) => {
    const template = SportConfigTemplates.line;
    const data = config == null ? void 0 : config[template];
    return data;
  }
);
const selectSportLineViewType = createSelector(
  getBetradarSetupState,
  (s, sportId) => sportId,
  (s, sportId, isLive) => isLive,
  (s, sportId, isLive) => {
    var _a;
    const template = isLive ? SportConfigTemplates.live : SportConfigTemplates.line;
    const data = (_a = s.config) == null ? void 0 : _a[template][sportId];
    if (data == null ? void 0 : data.metaData) {
      return data.metaData.viewType;
    }
    return null;
  }
);
const fetchMarketDescription = () => ({
  type: getBetradarSetupMarketStart.type
});
const fetchMarketDescriptionEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBetradarSetupMarketStart.type),
  switchMap(
    (params) => Api.sportData.getMarketDescription(params).pipe(
      switchMap((data) => startWorker({ task: SportWorkerTasks.MakeMarketGroups, payload: data })),
      map((data) => getBetradarSetupMarketSuccess(
        data
      )),
      catchError((err) => {
        return of(getBetradarSetupMarketFailure(err));
      })
    )
  )
);
const fetchOutcomesDescription = () => ({
  type: getBetradarSetupOutcomesStart.type
});
const fetchOutcomesDescriptionEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBetradarSetupOutcomesStart.type),
  switchMap(() => Api.sportData.getOutcomesDescription().pipe(
    switchMap((data) => startWorker({ task: SportWorkerTasks.FormatOutcomesDescription, payload: data })),
    map((data) => getBetradarSetupOutcomesSuccess(
      data
    )),
    catchError((err) => of(getBetradarSetupOutcomesFailure(err)))
  ))
);
const fetchStatusDescription = () => ({
  type: getBetradarSetupStatusStart.type
});
const fetchVariantListDescriptionEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getVariantListStart.type),
  exhaustMap((action) => Api.sportData.getVariantTranslations(action.payload).pipe(
    map((data) => getBetradarVariantsSuccess(data.items)),
    catchError((err) => of(getVariantListFailed(err)))
  ))
);
const fetchCompetitorList = (payload) => ({
  type: getCompetitorListStart.type,
  payload
});
const fetchCompetitorListDescriptionEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCompetitorListStart.type),
  exhaustMap((action) => Api.sportData.getCompetitorTranslations(action.payload).pipe(
    map((data) => competitorsTranslationsSuccess(data.items)),
    catchError((err) => of(getCompetitorListFailed(err)))
  ))
);
const fetchVariantList = (payload) => ({
  type: getVariantListStart.type,
  payload
});
const fetchStatusDescriptionEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBetradarSetupStatusStart.type),
  switchMap(() => Api.sportData.getStatusDescription().pipe(
    map((data) => getBetradarSetupStatusSuccess(
      data
    )),
    catchError((err) => of(getBetradarSetupStatusFailure(err)))
  ))
);
const fetchGroupSorting = () => ({
  type: getBetRadarSportGroupSortingStart.type
});
const fetchGroupSortingEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBetRadarSportGroupSortingStart.type),
  switchMap(() => Api.sportData.getMarketSorting().pipe(
    pluck("rows"),
    map((data) => getBetRadarSportGroupSortingSuccess(
      data
    )),
    catchError((err) => of(getBetradarSetupStatusFailure(err)))
  ))
);
const fetchAlive = (params) => ({
  type: getBetradarAliveStart.type,
  payload: params
});
const fetchAliveEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBetradarAliveStart.type),
  switchMap(() => defer(() => Api.sportData.getFeedStatus()).pipe(
    map((data) => getBetradarAliveSuccess(data)),
    catchError(() => of(getBetRadarAliveClear())),
    refetchOnFocus()
  ))
);
const subscribeToEventData = (payload) => ({
  type: getSocketStart.type,
  payload
});
const isBot = () => {
  const ua = navigator.userAgent.toLowerCase();
  return /bot|crawl|spider|gmetrix|google|yandexbot|lighthouse|speed insights|pingdom/.test(ua);
};
const getSportSocketDataEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getSocketStart.type),
  filter(() => !isBot()),
  flatMap((action) => fromWebSocket(
    {
      namespace: action.payload.namespace,
      payload: action.payload.events,
      api: Api.wsClient
    },
    void 0,
    {
      keepInactive: action.payload.keepInactive,
      isProtoBuf: selectGrpcIsActive(state$.value)
    }
  ).pipe(
    map((data) => {
      data.type = data.event;
      return data;
    }),
    map((data) => getBetRadarSportDataSuccess(data)),
    takeUntil(action$.pipe(ofType(getBetRadarSportDataStop.type))),
    catchError((err) => of(getSocketFailed(err)))
  ))
);
const unsubscribeSportWs = () => ({
  type: unsubscribeSportSocketDataStart.type
});
const unsubscribeSportWsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(unsubscribeSportSocketDataStart.type),
  map(() => {
    Api.wsClient.unsubscribeAll(WSNamespaces.seasons);
    Api.wsClient.unsubscribeAll(WSNamespaces.stages);
    Api.wsClient.unsubscribeAll(WSNamespaces.leagues);
    return unsubscribeSportSocketDataSuccess();
  })
);
const unsubscribeSportEventsWs = (payload) => ({
  type: unsubscribeSportEventsDataStart.type,
  payload
});
const unsubscribeSportEventsWsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(unsubscribeSportEventsDataStart.type),
  map((action) => {
    Api.wsClient.unsubscribeMany({
      namespace: action.payload.namespace,
      channels: action.payload.events
    });
    return unsubscribeSportEventsDataSuccess();
  })
);
const getSocketPublicDataSubscribe = () => ({
  type: getSocketPublicData.type
});
const getSocketPublicDataSubscribeEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getSocketPublicData.type),
  flatMap(() => fromWebSocket(
    {
      namespace: WSNamespaces.public,
      api: Api.wsClient
    },
    void 0,
    {
      isProtoBuf: selectGrpcIsActive(state$.value)
    }
  ).pipe(
    map((data) => {
      data.type = data.event;
      return data;
    }),
    map((data) => getBetRadarSportDataSuccess(data)),
    takeUntil(action$.pipe(ofType(getBetRadarSportDataStop.type))),
    catchError((err) => of(getSocketFailed(err)))
  ))
);
const generateBetRadarGroupSortingTableData = (data = []) => {
  const mappedSports = data.reduce((acc, sportMarket) => {
    const {
      sportId,
      group: { name, id, order },
      markets
    } = sportMarket;
    acc.push({
      order,
      sportId,
      groupId: { name: `${id}` },
      name: { name: `${name}` },
      groupIdNumber: id,
      vendorMarketIds: {
        name: markets.map(({ vendorMarketId }) => vendorMarketId).join(",")
      }
    });
    return acc;
  }, []);
  return mappedSports;
};
const fetchSportConfig = () => ({
  type: getSportConfigStart.type
});
const fetchSportConfigEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getSportConfigStart.type),
  map(() => selectCustomerIpCountry(state$.value)),
  switchMap((countryCode) => Api.sportData.getSportConfig({ countryCode }).pipe(
    map(({ markets, showPeriodData }) => ({ ...markets, showPeriodData })),
    map((data) => getSportConfigSuccess(data)),
    catchError(() => of(getSportConfigError()))
  ))
);
export {
  betradarSetupReducer,
  betradarSetupSlice,
  competitorsTranslationsSuccess,
  fetchAlive,
  fetchAliveEpic,
  fetchCompetitorList,
  fetchCompetitorListDescriptionEpic,
  fetchGroupSorting,
  fetchGroupSortingEpic,
  fetchMarketDescription,
  fetchMarketDescriptionEpic,
  fetchOutcomesDescription,
  fetchOutcomesDescriptionEpic,
  fetchSportConfig,
  fetchSportConfigEpic,
  fetchStatusDescription,
  fetchStatusDescriptionEpic,
  fetchVariantList,
  fetchVariantListDescriptionEpic,
  generateBetRadarGroupSortingTableData,
  getBetRadarAliveClear,
  getBetRadarAliveFail,
  getBetRadarSportDataStop,
  getBetRadarSportGroupSortingStart,
  getBetRadarSportGroupSortingSuccess,
  getBetradarAliveStart,
  getBetradarAliveSuccess,
  getBetradarSetupMarketFailure,
  getBetradarSetupMarketStart,
  getBetradarSetupMarketSuccess,
  getBetradarSetupOutcomesFailure,
  getBetradarSetupOutcomesStart,
  getBetradarSetupOutcomesSuccess,
  getBetradarSetupState,
  getBetradarSetupStatusFailure,
  getBetradarSetupStatusStart,
  getBetradarSetupStatusSuccess,
  getBetradarVariantsSuccess,
  getCompetitorListFailed,
  getCompetitorListStart,
  getSocketFailed,
  getSocketPublicData,
  getSocketPublicDataSubscribe,
  getSocketPublicDataSubscribeEpic,
  getSocketStart,
  getSocketSuccess,
  getSportConfigError,
  getSportConfigStart,
  getSportConfigSuccess,
  getSportSocketDataEpic,
  getVariantListFailed,
  getVariantListStart,
  initialBetradarSetupState,
  selectAliveFeedsState,
  selectAliveState,
  selectBetRadarGroups,
  selectBetRadarMarketsEntities,
  selectBetRadarOutcomesEntities,
  selectBetRadarSetupLoaded,
  selectBetRadarSportStarted,
  selectBetRadarVariants,
  selectLiveBetsEnabled,
  selectLiveOddsState,
  selectOutcomeCompetitors,
  selectSportConfig,
  selectSportConfigBySportId,
  selectSportConfigLoaded,
  selectSportConfigMarketsBySportIds,
  selectSportConfigTitlesMap,
  selectSportLineViewType,
  selectSportSorted,
  selectSportSortedById,
  selectSportsWithAdditionalSection,
  selectStatusById,
  setBetRadarActiveEvents,
  subscribeToEventData,
  unsubscribeSportEventsDataError,
  unsubscribeSportEventsDataStart,
  unsubscribeSportEventsDataSuccess,
  unsubscribeSportEventsWs,
  unsubscribeSportEventsWsEpic,
  unsubscribeSportSocketDataError,
  unsubscribeSportSocketDataStart,
  unsubscribeSportSocketDataSuccess,
  unsubscribeSportWs,
  unsubscribeSportWsEpic,
  updateAliveStatus,
  updateLiveBetsEnabled,
  updateLiveOdds
};
